import React from 'react';
import { Camera, Users, Clock, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { MEDIA_CONFIG } from '../data/media';
import ContactButton from '../components/ContactButton';

const STATS = [
  {
    icon: Clock,
    number: "10+",
    label: "Jahre Erfahrung"
  },
  {
    icon: Users,
    number: "200+",
    label: "Zufriedene Kunden"
  },
  {
    icon: Camera,
    number: "500+",
    label: "Produktionen"
  },
  {
    icon: Heart,
    number: "100%",
    label: "Leidenschaft"
  }
];

function About() {
  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <div className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <img
          src={`${MEDIA_CONFIG.paths.backgrounds}/headers/about-header.jpg`}
          alt="Filmemacher bei der Arbeit"
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Über mich</h1>
            <p className="text-xl max-w-2xl mx-auto px-4">
              Professionelle Filmproduktion mit Leidenschaft für visuelle Geschichten
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="flex justify-center mb-4">
                  <stat.icon size={32} />
                </div>
                <div className="text-3xl font-bold mb-2">{stat.number}</div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Name Story Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-zinc-900 rounded-lg overflow-hidden"
          >
            <div className="grid md:grid-cols-2 gap-8">
              <div className="p-8">
                <h2 className="text-3xl font-bold mb-6">Warum HonzaMedia?</h2>
                <div className="space-y-4 text-gray-300">
                  <p>
                    Honza – die tschechische Koseform von Jan. So wie „Hans" für „Johann". 
                    Aber Honza ist mehr als nur ein Name. In tschechischen Märchen ist er der 
                    clevere Held, der mit Witz, Charme und einem Hauch Chaos seine Abenteuer 
                    besteht. Passt doch perfekt für einen Filmemacher, oder?
                  </p>
                  <p>
                    Und dann gibt's noch Jan – ein Name mit Geschichte. Jan Hus, 
                    Jan Žižka, Jan Roháč z Dubé – große Persönlichkeiten, die Geschichte 
                    geschrieben haben. Ob ich in ihre Fußstapfen trete? Nun ja, meine 
                    Kämpfe sind eher mit Licht und Schatten, Kamera und Schnittprogramm. 
                    Aber hey, jeder erzählt seine eigenen Geschichten.
                  </p>
                </div>
              </div>
              <motion.div 
                className="relative h-full min-h-[300px] md:min-h-0"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/media/images/ueber/profile.jpg"
                  alt="Jan Čtvrtníček - Filmemacher"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/20 transition-opacity duration-300 hover:opacity-0"></div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Journey Section */}
      <div className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-center mb-12">
              Von der Kindheitskamera zur Filmproduktion
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-black/30 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-accent-400">
                  📽 Aufgewachsen mit Film
                </h3>
                <p className="text-gray-300">
                  Meine Eltern führten in Prag das Studio 8, also war Film für mich 
                  von klein auf Alltag. Kamera geschnappt, erste Kurzfilme gedreht – 
                  der Rest ist Geschichte.
                </p>
              </div>

              <div className="bg-black/30 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-accent-400">
                  🎓 Von der Leidenschaft zum Beruf
                </h3>
                <p className="text-gray-300">
                  Mit einem Studium in Medieninformatik & Gestaltung verband ich 
                  kreative Vision mit technischer Perfektion. Heute bringe ich als 
                  selbstständiger Filmemacher, Kameramann und Cutter Geschichten 
                  auf den Bildschirm.
                </p>
              </div>

              <div className="bg-black/30 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-accent-400">
                  🎬 Mein Ansatz
                </h3>
                <p className="text-gray-300">
                  Jedes Projekt braucht eine eigene Handschrift. Mit innovativer 
                  Bildsprache und modernster Technik entwickle ich maßgeschneiderte 
                  Konzepte, die nicht nur gut aussehen, sondern Emotionen wecken.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-6">
              Lass uns dein Projekt zum Leben erwecken!
            </h2>
            <ContactButton className="mt-8" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default About;